import { DetailsList, Icon, IconButton, SelectionMode } from "@fluentui/react";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import AddProject from "../../Panel/Project/AddProject";
import AddTransaction from "../../Panel/Transaction/AddTransaction";
import AddTentativeTransaction from "../../Panel/Transaction/AddTentativeTransaction";
import ProjectLink from "../../Components/ProjectLink";
import GroupMail from "../../Components/GroupMail";
import { MoneyField } from "../../Utilities/MoneyField";
import { CSVDownloader } from "react-papaparse";
import EditProject from "../../Panel/Project/EditProject";
import { ValueField } from "../../Utilities/ValueField";
import ImportVentureData from "../../Panel/Venture/ImportVentureData";
import { netGrossColumn, netGrossField, netGrossFieldName, useNetGross } from "../../Utilities/NetGrossField";
import AuthContext from "../../Services/AuthContext";

function Projects() {
    const { actions } = useOutletContext();
    const [user] = useContext(AuthContext);
    const [refresh, setRefresh] = useState(0);
    const [projects, setProjects] = useState([]);
    const netGross = useNetGross();
    const params = useParams();

    useEffect(() => {
        const abortController = new AbortController();
        async function fetchData() {
            await fetch(`/api/venture/${params.id}`, {
                method: 'GET',
                signal: abortController.signal,
                headers: {
                    'Authorization': 'Bearer ' + user.token
                }
            }).then(res => res.json())
                .then(data => {
                    if (data[0].id !== null) {
                        setProjects(data);
                    } else {
                        setProjects([]);
                    }
                })
                .catch(err => { console.log(err) })
        };
        fetchData();
        return () => abortController.abort();
    }, [refresh, params.id]);

    useEffect(() => {
        actions([
            {
                key: "groupMail",
                text: "Mail Grupowy",
                iconProps: { iconName: "PublicEmail" },
                onRender: () => <GroupMail people={projects} />,
            },
            {
                key: "importVentureData",
                text: "Import",
                iconProps: { iconName: "Upload" },
                onClick: () => setImportVentureData(params.id),
            },
            {
                key: "addProject",
                text: "Dodaj Projekt",
                iconProps: { iconName: "Add" },
                onClick: () => setAddProject({ venture_id: params.id }),
            },

        ]);
        return actions;
    }, [params.id, projects]);

    const [addProject, setAddProject] = useState(false);
    const [editProject, setEditProject] = useState(false);
    const [addTransaction, setAddTransaction] = useState(false);
    const [addTentativeTransaction, setAddTentativeTransaction] = useState(false);
    const [importVentureData, setImportVentureData] = useState(false);

    const transactionField = (item) => {
        if (item.reservation_number === null || item.reservation_number === "") {
            const transactionButton = (<IconButton iconProps={{ iconName: "ScheduleEventAction" }} title="Rezerwuj"
                                                   onClick={() => setAddTransaction(item)} />);
            if (item.reserved_until !== null) {
                return (<div>{transactionButton}<br />{item.person}</div>);
            }
            return transactionButton;
        }
        return (<div
            title={`Przekazanie: ${item.transfer_date}\nFinalizacja umowy: ${item.finalization_date}`}>{item[netGrossField('transaction_')]}<br />{item.person}
        </div>);
    };

    const reservationField = (item) => {
        const preReserve = (<IconButton iconProps={{ iconName: "EventTentative" }} title="Wstępna Rezerwacja"
                                        onClick={() => setAddTentativeTransaction(item)} />);
        if (item.reserved_until === null) {
            return preReserve;
        }
        const until = new Date(item.reserved_until);
        const today = new Date();
        const warning = (until.getTime() < today.getTime() && item.transaction_date === null)
            ? (<Icon iconName="Warning12" style={{ "color": "orangered" }} />) : "";
        if (item.reservation_number === null) {
            return (<div>{preReserve}<br /> do {warning}{item.reserved_until}</div>);
        }
        return (<div
            title={`Ważne do ${item.reserved_until}`}>{warning}{item.reservation_number}<br />{item.reservation_date}
        </div>);
    };

    const scheduleCheckField = (item) => {
        if (parseFloat(item.schedule_check) !== 0.00) {
            return (<Icon iconName={"Warning12"} style={{ "color": "red" }} />);
        }
        return (<Icon iconName={"CheckMark"} />);
    };
    const [columns, setColumns] = useState([]);
    useEffect(() => {
        setColumns([
            {
                key: "name",
                name: "Projekt",
                ariaLabel: "Nazwa utworzonego projektu",
                fieldName: "name",
                minWidth: 200,
                isRowHeader: true,
                isResizable: false,
                onRender: ProjectLink,
            },
            {
                key: "divider",
                name: "Rozmiar",
                ariaLabel: "Rozmiar projektu",
                fieldName: "divider",
                minWidth: 60,
                isResizable: false,
                ...ValueField(),
            },
            {
                key: "internal_number",
                name: "Numer Wewnętrzny",
                ariaLabel: "Numer Wewnętrzny",
                fieldName: "internal_number",
                minWidth: 60,
                isResizable: true,
            },
            {
                minWidth: 120,
                ...netGrossColumn('project_', 'value', 'Wartość ')
            },
            {
                key: "transaction_value",
                name: `Transakcja ${netGrossFieldName()}`,
                ariaLabel: "Aktualna wartość transakcji",
                fieldName: "transaction_value",
                minWidth: 120,
                isResizable: true,
                onRender: transactionField,
            },
            {
                key: "reservation",
                name: "Rezerwacja",
                ariaLabel: "Dane Rezerwacji",
                minWidth: 120,
                isResizable: true,
                onRender: reservationField,
            },
            {
                key: "schedule_check",
                name: "Raty",
                ariaLabel: "Raty",
                fieldName: "schedule_check",
                minWidth: 70,
                onRender: scheduleCheckField,
            },
            {
                key: "paid",
                name: "Opłacono",
                ariaLabel: "Wartość opłaconych płatności",
                fieldName: "paid",
                minWidth: 160,
                isResizable: true,
                ...MoneyField(),
            },
            {
                key: "left",
                name: "Pozostało",
                ariaLabel: "Wartość pozostałych płatności",
                fieldName: "left",
                minWidth: 160,
                isResizable: true,
                ...MoneyField(),
            },
            {
                key: "outdate_payments",
                name: "Opóźnione",
                ariaLabel: "Wartość opóźnionych płatności",
                fieldName: "outdate_payments",
                minWidth: 160,
                isResizable: true,
                ...MoneyField(),
            },
            {
                key: "edit",
                name: "Akcje",
                ariaLabel: "Akcje",
                fieldName: "edit",
                minWidth: 40,
                maxWidth: 40,
                onRender: (item) => (<span>
                <IconButton iconProps={{ iconName: "edit" }} title="Edytuj" onClick={() => setEditProject(item)} />
            </span>),
            },
        ]);
    }, [netGross]);

    const _refresh = () => setRefresh((prev) => prev + 1);
    return (<>
        <DetailsList items={projects} columns={columns} selectionMode={SelectionMode.none} />
        <CSVDownloader data={projects} filename={`projekty_${params.id}`} bom={true}
                       config={{ delimiter: ";" }}><IconButton iconProps={{ iconName: "ExcelDocument" }}
                                                               title="Export CSV" /></CSVDownloader>
        <AddProject add={addProject} dismissPanel={() => setAddProject(false)}
                    onSuccess={_refresh} />
        <AddTransaction add={addTransaction} dismissPanel={() => setAddTransaction(false)}
                        onSuccess={_refresh} />
        <AddTentativeTransaction add={addTentativeTransaction} dismissPanel={() => setAddTentativeTransaction(false)}
                                 onSuccess={_refresh} />
        <EditProject edit={editProject} dismissPanel={() => setEditProject(false)}
                     onSuccess={_refresh} />
        <ImportVentureData data={importVentureData} endpoint={`/api/venture/${importVentureData}/payment/schedule/csv_import`} dismissPanel={() => setImportVentureData(false)}
                           onSuccess={_refresh} />
    </>);
}

export default Projects;